'use client'

import type { ApplicationInsights, ITelemetryItem } from '@microsoft/applicationinsights-web'
import { useEffect } from 'react'
import { redactTelemetryItem, redactUrl } from '../lib/telemetry-redaction'
import { operationIdVanAppInsights } from '../lib/fout-referentie'
import { rememberTelemetryBeaconConfig, rememberTelemetryBeaconUser } from '../lib/telemetry-beacon'

/**
 * Module-singleton: zodra <ClientTelemetryProvider> de SDK heeft geinit, kan
 * elke error-boundary (ook eentje buiten de React-tree, zoals global-error)
 * hier z'n exception naartoe sturen zonder React-context nodig te hebben.
 */
let appInsights: ApplicationInsights | null = null

// De ingelogde gebruiker kan al bekend zijn vóór de (lui-geladen) SDK klaar is;
// we onthouden 'm en passen 'm toe zodra de SDK er is (en bij elke wijziging).
// `undefined` = nog nooit gezet -> niets doen.
let pendingUserId: string | null | undefined

function applyUserContext(): void {
  if (!appInsights || pendingUserId === undefined) return
  if (pendingUserId) appInsights.setAuthenticatedUserContext(pendingUserId, undefined, false)
  else appInsights.clearAuthenticatedUserContext()
}

/**
 * Koppelt (of ontkoppelt) de telemetrie aan een gebruiker via een OPAAK id:
 * intern de medewerker-`User`-id, klant de pseudonieme `ClientPortalUser`-id.
 * NOOIT naam/e-mail. `null` wist de koppeling (bv. bij uitloggen). Cookieless
 * (`storeInCookie=false`) - geen extra cookie-consent nodig.
 */
export function setTelemetryUser(userId: string | null): void {
  pendingUserId = userId
  // Ook voor de beacon-route (global-error), zodat die events even herleidbaar
  // zijn naar een gebruiker als de SDK-events.
  rememberTelemetryBeaconUser(userId)
  applyUserContext()
}

/**
 * Stuurt een client-side exception naar Application Insights. No-op zolang de
 * provider de SDK nog niet heeft geinit (bv. in global-error wanneer de
 * root-layout zelf faalde). React-error-boundaries "slikken" render-fouten -
 * die bereiken window.onerror niet - dus de boundaries moeten dit expliciet
 * aanroepen, anders blijft een render-crash een blinde vlek.
 *
 * FO-364 - retourneert de operation-/trace-id waaronder het exception-event is
 * verstuurd (of `null` zolang de SDK niet klaar is). De error-boundaries tonen
 * die id als opaque fout-referentie ("ref: ...") zodat een gebruikersmelding
 * via `AppExceptions | where OperationId == '<ref>'` terug te vinden is. De id
 * bevat zelf geen foutdetail (geen stack, geen PII).
 */
export function trackClientException(
  error: unknown,
  properties?: Record<string, string>,
): string | null {
  if (!appInsights) return null
  const exception = error instanceof Error ? error : new Error(String(error))
  appInsights.trackException({ exception, properties })
  return operationIdVanAppInsights(appInsights)
}

/**
 * Verstuurt een custom event naar Application Insights. No-op zolang de SDK nog
 * niet is geinit. Gebruikt voor verwachte, auto-herstelde signalen zoals een
 * ChunkLoadError-reload na een deploy - die horen niet als "exception" de
 * foutdashboards te vervuilen, maar zijn wel waardevol om de deploy-churn te
 * meten.
 */
export function trackClientEvent(name: string, properties?: Record<string, string>): void {
  if (!appInsights) return
  appInsights.trackEvent({ name }, properties)
}

/**
 * Of de (lui-geladen) SDK al is geinit. Callers die een event gegarandeerd
 * afgeleverd willen hebben (bv. vlak vóór een reload) kiezen hiermee tussen de
 * SDK-route (rijke context) en de sendBeacon-fallback uit telemetry-beacon.ts.
 */
export function isClientTelemetryReady(): boolean {
  return appInsights !== null
}

/**
 * Verstuurt de gebufferde telemetrie direct (synchroon), voor gebruik vlak vóór
 * een `window.location.reload()`: de SDK batcht events en een reload gooit die
 * buffer anders weg, waardoor bv. het chunk-reload-event nooit aankwam. No-op
 * zolang de SDK niet is geinit; flushen is best-effort.
 */
export function flushClientTelemetry(): void {
  if (!appInsights) return
  try {
    // onunloadFlush routeert via sendBeacon/fetch-keepalive en blokkeert de
    // main thread niet. flush(false) zou een synchrone XHR doen die op een
    // hangend mobiel netwerk seconden blokkeert - precies het scenario
    // (PWA-resume, zwak 4G) waarin deze flush gebruikt wordt.
    appInsights.onunloadFlush()
  } catch {
    // best-effort: een mislukte flush mag de reload nooit blokkeren
  }
}

export interface ClientTelemetryProviderProps {
  /**
   * App Insights connection-string, server-side gelezen uit
   * `process.env.APPLICATIONINSIGHTS_CONNECTION_STRING` en als prop doorgegeven
   * (dus niet via NEXT_PUBLIC / de build). Leeg/undefined (lokaal dev) => de
   * provider doet niets.
   */
  connectionString?: string
  /** AppRoleName voor de browser-telemetrie, bv. 'streep-backoffice'. */
  cloudRole?: string
  /** App-versie voor de `ai.application.ver`-tag. */
  appVersion?: string
  children: React.ReactNode
}

/**
 * Initialiseert de Application Insights JS-SDK client-side, zodat client-side
 * fouten (window.onerror, unhandled promise rejections, ChunkLoadError en
 * render-crashes via de boundaries) en RUM (page views, web-vitals,
 * AJAX-timings) in dezelfde App-Insights-resource landen als de server-side
 * OTel. Sluit de blinde vlek dat client-side fouten nergens zichtbaar waren.
 *
 * Hardening: cookieless (geen cookie-consent nodig), een telemetry-initializer
 * die query-strings én token-/id-pad-segmenten redigeert via redactTelemetryItem
 * (PII zoals de klant-onboarding-token), en de connection-string loopt via de
 * server-layout (niet in de client-build). De SDK (~70KB) wordt lui geimporteerd
 * binnen het effect zodat hij niet op het kritieke pad van de mobile-first PWA
 * staat maar pas ná hydration laadt.
 */
export function ClientTelemetryProvider({
  connectionString,
  cloudRole,
  appVersion,
  children,
}: ClientTelemetryProviderProps) {
  // Stempelt elke zichtbaar-wording van de app (incl. resume uit de iOS-app-
  // switcher) in sessionStorage, zodat global-error kan bepalen of een fout in
  // het opstartvenster valt (transient opstartfoutje -> stille herstart) of
  // midden in het gebruik (echte foutkaart). Bewust vóór en los van de
  // connection-string-guard: dit moet ook zonder telemetrie-config werken.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const stempelZichtbaar = () => {
      if (document.visibilityState !== 'visible') return
      try {
        window.sessionStorage.setItem('streep:visible-at', String(Date.now()))
      } catch {
        // geen sessionStorage - het opstartvenster valt terug op performance.now()
      }
    }
    stempelZichtbaar()
    document.addEventListener('visibilitychange', stempelZichtbaar)
    // iOS vuurt bij een bfcache-/freeze-restore betrouwbaarder 'pageshow' dan
    // 'visibilitychange' - samen dekken ze het resume-uit-de-app-switcher-pad.
    window.addEventListener('pageshow', stempelZichtbaar)
    return () => {
      document.removeEventListener('visibilitychange', stempelZichtbaar)
      window.removeEventListener('pageshow', stempelZichtbaar)
    }
  }, [])

  useEffect(() => {
    if (!connectionString || appInsights || typeof window === 'undefined') return

    // Vóór de (lui-geladen) SDK-import, zodat de beacon-config er óók is wanneer
    // juist die import faalt (iOS-resume) en global-error moet rapporteren.
    rememberTelemetryBeaconConfig(connectionString, cloudRole, appVersion)

    let cancelled = false
    void import('@microsoft/applicationinsights-web').then(({ ApplicationInsights }) => {
      if (cancelled || appInsights) return

      const ai = new ApplicationInsights({
        config: {
          connectionString,
          // Cookieless: geen ai_user/ai_session-cookies => geen cookie-consent nodig.
          disableCookiesUsage: true,
          // RUM: SPA-route-changes als page views + bezoektijd + AJAX/dependency-timings.
          enableAutoRouteTracking: true,
          autoTrackPageVisitTime: true,
          disableFetchTracking: false,
          // Unhandled promise rejections meenemen (window.onerror-exceptions staan default aan).
          enableUnhandledPromiseRejectionTracking: true,
        },
      })
      ai.loadAppInsights()

      ai.addTelemetryInitializer((envelope: ITelemetryItem) => {
        // Browser-telemetrie herkenbaar onder dezelfde role als de server-app.
        if (!envelope.tags) envelope.tags = []
        const tags = envelope.tags as unknown as Record<string, string>
        if (cloudRole) tags['ai.cloud.role'] = cloudRole
        if (appVersion) tags['ai.application.ver'] = appVersion
        // Defense-in-depth: mocht ai.operation.name op dit punt toch al gezet zijn
        // (de load-bearing redactie zit op ext.trace.name in redactTelemetryItem).
        if (typeof tags['ai.operation.name'] === 'string') {
          tags['ai.operation.name'] = redactUrl(tags['ai.operation.name'])
        }
        // PII-redactie: ext.trace.name (-> wire ai.operation.name) + de URL-velden
        // in baseData + exception-messages. Houdt het onboarding-token uit AI.
        redactTelemetryItem(envelope)
        return true
      })

      ai.trackPageView()
      appInsights = ai
      applyUserContext() // pas een al-bekende gebruiker toe nu de SDK er is
    })

    return () => {
      cancelled = true
    }
  }, [connectionString, cloudRole, appVersion])

  return <>{children}</>
}

/**
 * Mount dit (client) component in de authed layout met de id van de ingelogde
 * gebruiker; het koppelt de telemetrie aan die gebruiker zodra de SDK klaar is.
 * Rendert niets. Intern: `User.id`; klant: pseudonieme `ClientPortalUser`-id.
 */
export function TelemetryUser({ userId }: { userId: string | null }) {
  useEffect(() => {
    setTelemetryUser(userId)
    // Cleanup bij unmount (= de authed shell verlaten, bv. uitloggen): zet de
    // koppeling expliciet op null, anders zou een fout op de login-pagina nog
    // aan de vórige gebruiker worden toegeschreven (SDK-context én beacon).
    return () => setTelemetryUser(null)
  }, [userId])
  return null
}
